import { useCallback, useEffect, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { ExpressPaymentType } from '@stripe/stripe-js'

import { WalletCheckoutSession } from 'src/Components/WalletExpressCheckout/WalletExpressCheckoutForm'
import {
  CouponApplyResult,
  ScheduleSummaryProps,
} from 'src/Components/ScheduleSummary'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useCurrency } from 'src/Hooks/useCurrency'
import { getFormattedCheckoutTimer } from 'src/Redux/checkoutCountdown-process'
import {
  getIsEnoughBalanceForTiktok,
  getTiktokPrice,
} from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { useBonusTooltipTimer } from 'src/Hooks/useBonusTooltip'
import { getCouponToKeep, getPayableCoupon } from 'src/Hooks/useThresholdCoupon'
import { useGetBillingAgreementQuery } from 'src/Redux/billingAgreementApi'
import { getArtistBalance } from 'src/Redux/auth-process/userSlice/selectors'
import { useOrderCheckoutConfirmEvents } from 'src/Hooks/useOrderCheckoutConfirmEvents'
import { PaymentMethodEnum } from 'src/Constants/enums'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import { getOrderCheckoutPaymentError } from 'src/Utils/orderCheckoutPaymentError'
import { refetchUsersMe } from 'src/Redux/userInfoApi'
import { ScheduleSteps } from 'src/Types/ui'
import { getDefaultStartDate } from 'src/Constants/functions'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { createPaymentUrls } from 'src/Hooks/useStripePayment'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import {
  OrderCheckoutInfoResponse,
  OrderPaymentMethod,
  useCreateOrderCheckoutMutation,
  useGetCurrentOrderQuery,
  useLazyGetOrderCheckoutInfoQuery,
  useUpdateOrderItemMutation,
} from 'src/Redux/artistOrderApi'
import {
  getIsCoveredByBalance,
  getIsOrderFullyCovered,
} from 'src/Utils/displayedPaymentMethods'

import { useTikTokOrderSync } from './useTikTokOrderSync'
import { useTikTokPrioritizedLaunch } from './useTikTokPrioritizedLaunch'
import { useTikTokThresholdCoupon } from './useTikTokThresholdCoupon'

const ORDER_PAYMENT_METHOD_MAP: Record<PaymentMethodEnum, OrderPaymentMethod> =
  {
    [PaymentMethodEnum.Credits]: OrderPaymentMethod.BalanceOnly,
    [PaymentMethodEnum.STRIPE]: OrderPaymentMethod.StripeOneTime,
    [PaymentMethodEnum.PAYPAL]: OrderPaymentMethod.PaypalOneTime,
    [PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT]:
      OrderPaymentMethod.PaypalBillingAgreement,
    [PaymentMethodEnum.APPLE_PAY]: OrderPaymentMethod.StripeWallet,
    [PaymentMethodEnum.GOOGLE_PAY]: OrderPaymentMethod.StripeWallet,
  }

const ORDER_PAYMENT_METHOD_TO_ENUM: Partial<
  Record<OrderPaymentMethod, PaymentMethodEnum>
> = {
  [OrderPaymentMethod.BalanceOnly]: PaymentMethodEnum.Credits,
  [OrderPaymentMethod.StripeOneTime]: PaymentMethodEnum.STRIPE,
  [OrderPaymentMethod.PaypalOneTime]: PaymentMethodEnum.PAYPAL,
  [OrderPaymentMethod.PaypalBillingAgreement]:
    PaymentMethodEnum.PAYPAL_BILLING_AGREEMENT,
}

const findCouponError = (
  data: OrderCheckoutInfoResponse,
  couponCode: string,
): CouponApplyResult['coupon']['error'] => {
  const topLevelError = data.couponErrors.find(
    (item) => item.couponCode === couponCode,
  )

  if (topLevelError?.error) {
    return { message: topLevelError.error, param: topLevelError.param ?? null }
  }

  const itemDiscount = data.items
    .flatMap((item) => item.discounts)
    .find((discount) => discount.couponCode === couponCode)

  if (itemDiscount?.error) {
    return { message: itemDiscount.error, param: itemDiscount.param ?? null }
  }

  return null
}

const hasCouponImmediateDiscount = (
  data: OrderCheckoutInfoResponse,
  couponCode: string,
): boolean =>
  data.items
    .flatMap((item) => item.discounts)
    .some((d) => d.couponCode === couponCode && d.discountAmount > 0)

const mapToCouponApplyResult = (
  data: OrderCheckoutInfoResponse,
  couponCode: string,
): CouponApplyResult => {
  const error = findCouponError(data, couponCode)
  return {
    coupon: {
      error,
      isBonusCoupon: !error && !hasCouponImmediateDiscount(data, couponCode),
    },
  }
}

const resolveOrderPaymentMethod = (
  paymentMethod: PaymentMethodEnum,
  isRememberPayPalForFuture: boolean,
  paypalEmail?: string,
): OrderPaymentMethod => {
  if (
    paymentMethod === PaymentMethodEnum.PAYPAL &&
    isRememberPayPalForFuture &&
    !paypalEmail
  ) {
    return OrderPaymentMethod.PaypalBillingAgreement
  }
  return (
    ORDER_PAYMENT_METHOD_MAP[paymentMethod] || OrderPaymentMethod.BalanceOnly
  )
}

const getShowPromocode = (
  isOrderFullyCovered: boolean,
  hasDiscount: boolean,
): boolean => !isOrderFullyCovered && !hasDiscount

const toCouponList = (coupon: string): string[] => (coupon ? [coupon] : [])

const getCheckoutCoupon = (
  balance: number,
  checkoutInfo: OrderCheckoutInfoResponse | null | undefined,
  appliedCoupon: string,
): string =>
  getIsCoveredByBalance(balance, checkoutInfo)
    ? ''
    : getPayableCoupon(checkoutInfo, appliedCoupon)

const WALLET_PAYMENT_METHOD_MAP: Partial<
  Record<ExpressPaymentType, PaymentMethodEnum>
> = {
  google_pay: PaymentMethodEnum.GOOGLE_PAY,
  apple_pay: PaymentMethodEnum.APPLE_PAY,
}

type UseTikTokScheduleOrderSummary = ScheduleSummaryProps & {
  availablePaymentMethods?: PaymentMethodEnum[]
  isOrderFullyCovered: boolean
  isSyncing: boolean
  isWalletPaymentEligible: boolean
  walletAmount: number
  onConfirmWalletPayment: (
    expressPaymentType: ExpressPaymentType,
  ) => Promise<WalletCheckoutSession | null>
  onWalletPaymentSuccess: () => void
  onWalletPaymentError: () => void
}

export const useTikTokScheduleOrderSummary =
  (): UseTikTokScheduleOrderSummary => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { number } = useParams()
    const { search } = useCustomSearchParams()
    const campaignId = Number(number)
    const isPaymentSucceeded = search.get('paymentStatus') === 'success'

    const paymentMethod = useAppSelector(getPaymentMethod)
    const isRememberPayPalForFuture = useAppSelector(
      getRememberPayPalForFutureFlag,
    )
    const { data: billingData } = useGetBillingAgreementQuery()
    const isEnoughBalance = useAppSelector(getIsEnoughBalanceForTiktok)
    const balance = useAppSelector(getArtistBalance)
    const countdown = useAppSelector(getFormattedCheckoutTimer)
    const tikTokPrice = useAppSelector(getTiktokPrice)
    const scheduledTikTokCampaign = useAppSelector(getScheduledTikTokCampaign)

    const budget = tikTokPrice || 0

    const [appliedCoupon, setAppliedCoupon] = useState('')

    const { startNextTimeBonusCountdown, isNextTimeBonus } =
      useBonusTooltipTimer({
        platform: CampaignPlatformTypeEnum.TIKTOK,
        budget,
      })

    const { calculateCurrencyValue } = useCurrency(
      CampaignPlatformTypeEnum.TIKTOK,
    )

    const triggerConfirmPaymentEvents = useOrderCheckoutConfirmEvents(
      CampaignPlatformTypeEnum.TIKTOK,
    )

    const { data: currentOrder, isFetching: isCurrentOrderFetching } =
      useGetCurrentOrderQuery()
    const [createOrderCheckout, { isLoading }] =
      useCreateOrderCheckoutMutation()
    const [triggerCheckoutInfo, { data: checkoutInfoData }] =
      useLazyGetOrderCheckoutInfoQuery()
    const [updateOrderItem] = useUpdateOrderItemMutation()

    const payableCoupon = getCheckoutCoupon(
      balance,
      checkoutInfoData,
      appliedCoupon,
    )

    const refetchCheckoutInfo = useCallback(async (): Promise<void> => {
      const data = await triggerCheckoutInfo({
        coupons: toCouponList(appliedCoupon),
      }).unwrap()

      if (appliedCoupon && getIsCoveredByBalance(balance, data)) {
        setAppliedCoupon('')
        await triggerCheckoutInfo({ coupons: [] }).unwrap()
      }
    }, [appliedCoupon, balance, triggerCheckoutInfo])

    const syncCheckoutInfo = useCallback(async (): Promise<void> => {
      await triggerCheckoutInfo({ coupons: [] }).unwrap()
    }, [triggerCheckoutInfo])

    useTikTokOrderSync({
      campaignId,
      budget,
      isPaymentSucceeded,
      currentOrder,
      onOrderSynced: syncCheckoutInfo,
      isOrderFetching: isCurrentOrderFetching,
    })

    useEffect(() => {
      startNextTimeBonusCountdown()
    }, [startNextTimeBonusCountdown])

    const onApplyPromocode = async (
      couponCode: string,
    ): Promise<CouponApplyResult | undefined> => {
      const data = await triggerCheckoutInfo({
        coupons: [couponCode],
      }).unwrap()
      if (!data) {
        return void 0
      }

      setAppliedCoupon(
        getCouponToKeep(findCouponError(data, couponCode), couponCode),
      )

      return mapToCouponApplyResult(data, couponCode)
    }

    const handleRemoveCoupon = (): void => {
      setAppliedCoupon('')
      void triggerCheckoutInfo({ coupons: [] })
    }

    const startDate =
      scheduledTikTokCampaign.startDate?.toString() || getDefaultStartDate()

    const { prioritizedLaunch } = useTikTokPrioritizedLaunch({
      campaignId,
      budget,
      startDate,
      calculateCurrencyValue,
      orderId: currentOrder?.id,
      orderItems: currentOrder?.items,
      orderStatus: currentOrder?.status,
      onAfterMutation: refetchCheckoutInfo,
    })

    const breakdown = checkoutInfoData?.breakdown
    const isOrderFullyCovered = getIsOrderFullyCovered(
      breakdown,
      isEnoughBalance,
    )

    const {
      thresholdCoupon,
      isSyncing,
      onApplyPromocode: onApplyPromocodeWithOffer,
    } = useTikTokThresholdCoupon({
      pricingModel: scheduledTikTokCampaign.pricingModel,
      formatAmount: calculateCurrencyValue,
      onRemoveCoupon: handleRemoveCoupon,
      campaignId,
      budget,
      appliedCoupon,
      isOrderFullyCovered,
      isPaymentSucceeded,
      currentOrder,
      checkoutInfoData,
      onApplyPromocode,
      updateOrderItem,
      triggerCheckoutInfo,
    })

    const navigateToPaymentSuccess = useCallback((): void => {
      navigate(
        `${ArtistRoutesEnum.TiktokSchedule}/${campaignId}?step=${ScheduleSteps.CHECKOUT}&paymentStatus=success&startDate=${encodeURIComponent(startDate)}`,
      )
      void dispatch(refetchUsersMe())
    }, [campaignId, dispatch, navigate, startDate])

    const handleClickContinue = useCallback((): void => {
      if (!checkoutInfoData || isSyncing) {
        return
      }

      const orderPaymentMethod = resolveOrderPaymentMethod(
        paymentMethod,
        isRememberPayPalForFuture,
        billingData?.paypalEmail,
      )

      if (!checkoutInfoData.paymentMethods.includes(orderPaymentMethod)) {
        dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
        return
      }

      const { successUrl, cancelUrl } = createPaymentUrls({
        campaignId,
        startDate,
        type: CampaignPlatformTypeEnum.TIKTOK,
        budget: checkoutInfoData.breakdown.totalFullPrice,
        coupon: payableCoupon,
      })

      const itemBreakdowns = checkoutInfoData.items.map((item) => ({
        id: item.id,
        fullPrice: item.fullPrice,
        appliedBalance: item.appliedBalance,
        payable: item.payable,
        itemDiscount: item.itemDiscount,
        vatAmount: item.vatAmount,
      }))

      triggerConfirmPaymentEvents()

      createOrderCheckout({
        paymentMethod: orderPaymentMethod,
        coupons: toCouponList(payableCoupon),
        itemBreakdowns,
        successUrl,
        cancelUrl,
      })
        .unwrap()
        .then((result) => {
          if (result.checkoutSessionUrl) {
            window.location.href = result.checkoutSessionUrl
            return
          }

          navigateToPaymentSuccess()
        })
        .catch((error: unknown) => {
          dispatch(setPaymentAlertError(getOrderCheckoutPaymentError(error)))
        })
    }, [
      payableCoupon,
      billingData?.paypalEmail,
      campaignId,
      checkoutInfoData,
      createOrderCheckout,
      dispatch,
      isSyncing,
      isRememberPayPalForFuture,
      navigateToPaymentSuccess,
      paymentMethod,
      startDate,
      triggerConfirmPaymentEvents,
    ])

    const handleWalletPaymentError = useCallback((): void => {
      dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
    }, [dispatch])

    const handleConfirmWalletPayment = useCallback(
      async (
        expressPaymentType: ExpressPaymentType,
      ): Promise<WalletCheckoutSession | null> => {
        if (isSyncing) {
          return null
        }

        if (
          !checkoutInfoData?.paymentMethods.includes(
            OrderPaymentMethod.StripeWallet,
          )
        ) {
          handleWalletPaymentError()
          return null
        }

        const { successUrl, cancelUrl } = createPaymentUrls({
          campaignId,
          startDate,
          type: CampaignPlatformTypeEnum.TIKTOK,
          budget: checkoutInfoData.breakdown.totalFullPrice,
          coupon: payableCoupon,
        })

        const itemBreakdowns = checkoutInfoData.items.map((item) => ({
          id: item.id,
          fullPrice: item.fullPrice,
          appliedBalance: item.appliedBalance,
          payable: item.payable,
          itemDiscount: item.itemDiscount,
          vatAmount: item.vatAmount,
        }))

        triggerConfirmPaymentEvents(
          WALLET_PAYMENT_METHOD_MAP[expressPaymentType],
        )

        try {
          const result = await createOrderCheckout({
            paymentMethod: OrderPaymentMethod.StripeWallet,
            coupons: toCouponList(payableCoupon),
            itemBreakdowns,
            successUrl,
            cancelUrl,
          }).unwrap()

          if (!result.checkoutSessionUrl) {
            handleWalletPaymentError()
            return null
          }

          return {
            clientSecret: result.checkoutSessionUrl,
            returnUrl: successUrl,
          }
        } catch {
          handleWalletPaymentError()
          return null
        }
      },
      [
        payableCoupon,
        campaignId,
        checkoutInfoData,
        createOrderCheckout,
        handleWalletPaymentError,
        isSyncing,
        startDate,
        triggerConfirmPaymentEvents,
      ],
    )

    const availablePaymentMethods = checkoutInfoData?.paymentMethods
      .map((method) => ORDER_PAYMENT_METHOD_TO_ENUM[method])
      .filter((method): method is PaymentMethodEnum => Boolean(method))

    const artistName =
      scheduledTikTokCampaign.track?.authorName ??
      scheduledTikTokCampaign.trackAuthorName ??
      ''
    const trackName =
      scheduledTikTokCampaign.track?.title ??
      scheduledTikTokCampaign.trackTitle ??
      ''

    return {
      artistName,
      availablePaymentMethods,
      isOrderFullyCovered,
      countdown,
      thresholdCoupon,
      isSyncing,
      trackName,
      prioritizedLaunch,
      onApplyPromocode: onApplyPromocodeWithOffer,
      matchingType: null,
      onConfirm: handleClickContinue,
      isWalletPaymentEligible: Boolean(
        checkoutInfoData?.paymentMethods.includes(
          OrderPaymentMethod.StripeWallet,
        ),
      ),
      walletAmount: breakdown?.totalPayable || 0,
      onConfirmWalletPayment: handleConfirmWalletPayment,
      onWalletPaymentSuccess: navigateToPaymentSuccess,
      onWalletPaymentError: handleWalletPaymentError,
      paymentLoading: isLoading,
      price: calculateCurrencyValue(breakdown?.totalFullPrice),
      discount: breakdown?.totalDiscount
        ? calculateCurrencyValue(breakdown.totalDiscount, true)
        : '',
      vat: breakdown?.totalVatAmount
        ? `+ ${calculateCurrencyValue(breakdown.totalVatAmount)}`
        : '',
      balance: `- ${calculateCurrencyValue(breakdown?.totalAppliedBalance)}`,
      total: calculateCurrencyValue(breakdown?.totalPayable),
      showPromocode: getShowPromocode(
        isOrderFullyCovered,
        Boolean(breakdown?.totalDiscount),
      ),
      showTooltip: isNextTimeBonus,
      platform: CampaignPlatformTypeEnum.TIKTOK,
    }
  }
